<script setup>
import layoutShell from '@/components/layout/layoutShell.vue'
import LayoutFooter from '@/components/layout/layoutFooter.vue'

import InputText from 'primevue/inputtext'
import MultiSelect from 'primevue/multiselect'
import Select from 'primevue/select'
import Editor from 'primevue/editor'
import Checkbox from 'primevue/checkbox'
import Button from 'primevue/button'
import { CronPrime } from '@vue-js-cron/prime'

import { ref, watch, defineProps, computed, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'

import { useUserStore } from '@/stores/user'
import { useUnsavedChangesStore } from '@/stores/unsavedChanges'
import { useBreadcrumbStore } from '@/stores/breadcrumb'
import { useI18n } from '@/i18n'
import { useConfirmDelete } from '@/composables/useConfirmDelete'
import api from '../../api'
import { useNotify } from '@/services/useNotify'
import AppDialog from '@/components/ui/AppDialog.vue'
import WorkflowDetailsHeader from '@/components/siem/workflow-details/WorkflowDetailsHeader.vue'
import WorkflowDetailsDescription from '@/components/siem/workflow-details/WorkflowDetailsDescription.vue'
import WorkflowDetailsTags from '@/components/siem/workflow-details/WorkflowDetailsTags.vue'
import WorkflowDetailsMitreSection from '@/components/siem/workflow-details/WorkflowDetailsMitreSection.vue'
import WorkflowDetailsIsacaSection from '@/components/siem/workflow-details/WorkflowDetailsIsacaSection.vue'
import WorkflowDetailsStreamsSection from '@/components/siem/workflow-details/WorkflowDetailsStreamsSection.vue'

const notify = useNotify()
const user = useUserStore()
const unsavedChanges = useUnsavedChangesStore()
const breadcrumbStore = useBreadcrumbStore()
const { activeLang, t, languageOptions } = useI18n()
const { confirmDelete } = useConfirmDelete()
const router = useRouter()
const canEdit = computed(() => user.can('siem_workflow.change'))

const props = defineProps({
  workflowId: { type: Number, required: true }
})

const workflow = ref(null)
const allTags = ref([])
const streamsLoading = ref(false)
const streamsError = ref('')
const mitreMappings = ref([])
const mitreLoading = ref(false)
const mitreError = ref('')
const isacaMappings = ref([])
const isacaLoading = ref(false)
const isacaError = ref('')

const showCoreDialog = ref(false)
const showAddStreamModal = ref(false)
const showConditionModal = ref(false)
const showMitreDialog = ref(false)
const showIsacaDialog = ref(false)
const mitreEditing = ref(false)
const isacaEditing = ref(false)

const editingWorkflowCore = ref({
  severity: 'INFO',
  tags: [],
  featured: false,
  visibility: 'community',
})

const workflowLang = ref(activeLang.value)
const workflowTranslations = ref({})
const workflowTranslationOriginals = ref({})
const baseWorkflowSnapshot = ref({ name: '', description: '' })

const availableStreams = ref([])
const availableTotal = ref(0)
const availableLoading = ref(false)
const availableError = ref('')
const streamPickerRef = ref(null)
const streamCategories = ref([])
const streamCategoryFilter = ref(null)
const streamScopeFilter = ref(null)
const streamCategoryOptions = computed(() => [
  { label: t('siemWorkflowDetails.streamFilterAllCategories'), value: null },
  ...streamCategories.value.map((c) => ({ label: c, value: c })),
])
const streamScopeOptions = computed(() => [
  { label: t('siemWorkflowDetails.streamFilterAllScopes'), value: null },
  { label: t('siemWorkflowDetails.streamScopeManaged'),    value: 'managed' },
  { label: t('siemWorkflowDetails.streamScopeCommunity'),  value: 'community' },
  { label: t('siemWorkflowDetails.streamScopeCompany'),    value: 'company' },
])
const streamPickerColumns = computed(() => [
  { field: 'name',         header: t('siemWorkflowDetails.streamColName'),        sortable: true,  default: true,  minWidth: '16rem' },
  { field: 'category',     header: t('siemWorkflowDetails.streamColCategory'),    sortable: true,  default: true },
  { field: 'subcategory',  header: t('siemWorkflowDetails.streamColSubcategory'), sortable: true,  default: true },
  { field: 'company_name', header: t('siemWorkflowDetails.streamColScope'),       sortable: true,  default: true },
  { field: 'guid',         header: t('siemWorkflowDetails.guidLabel'),            sortable: false, default: false },
  { field: 'actions',      header: '',                                            sortable: false, default: true },
])
const mitreTactics = ref([])
const mitreTechniques = ref([])
const isacaUseCases = ref([])
const conditionEdits = ref({})
const streamEdits = ref({})
const changesSaving = ref(false)
const conditionTarget = ref(null)
const conditionForm = ref({
  type: 'Filter',
  field: '',
  operator: 'eq',
  value: '',
  cron: ''
})
const mitreOriginal = ref({ tactic_id: null, technique_id: null })
const mitreForm = ref({
  tactic_id: null,
  technique_id: null,
  confidence: 'MEDIUM',
  rationale: ''
})
const isacaForm = ref({
  use_case_id: null,
  confidence: 'MEDIUM',
  rationale: ''
})

const emptyValue = '-'
const unsavedKey = 'workflow-details'

const stripHtmlTags = (html = '') => html.replace(/<\/?[^>]+(>|$)/g, '')

const englishFallbackFromWorkflow = (workflowItem) => ({
  name: workflowItem?.english_name || workflowItem?.name || '',
  description: workflowItem?.english_description || workflowItem?.description || '',
})

const resetTranslationDrafts = () => {
  const drafts = {}
  const originals = {}
  languageOptions.forEach((opt) => {
    drafts[opt.code] = { name: '', description: '' }
    originals[opt.code] = { name: '', description: '' }
  })
  workflowTranslations.value = drafts
  workflowTranslationOriginals.value = originals
}

const ensureTranslationDraft = (lang) => {
  if (!lang) return { name: '', description: '' }
  if (!workflowTranslations.value[lang]) {
    workflowTranslations.value[lang] = { name: '', description: '' }
  }
  if (!workflowTranslationOriginals.value[lang]) {
    workflowTranslationOriginals.value[lang] = { name: '', description: '' }
  }
  return workflowTranslations.value[lang]
}

const translationDraft = (lang) => ensureTranslationDraft(lang)

const setTranslationDraft = (lang, values = {}, { setOriginal = false, onlyIfEmpty = false } = {}) => {
  if (!lang) return
  const draft = ensureTranslationDraft(lang)
  const nextName = values.name ?? ''
  const nextDescription = values.description ?? ''
  if (!onlyIfEmpty || !draft.name) draft.name = nextName
  if (!onlyIfEmpty || !draft.description) draft.description = nextDescription
  if (setOriginal) {
    workflowTranslationOriginals.value[lang] = { name: draft.name, description: draft.description }
  }
}

const translationHasContent = (draft) => {
  if (!draft) return false
  const name = (draft.name || '').trim()
  const desc = stripHtmlTags(draft.description || '').trim()
  return Boolean(name || desc)
}

const translationChanged = (lang, draft) => {
  const original = workflowTranslationOriginals.value[lang] || { name: '', description: '' }
  return (draft.name || '') !== (original.name || '') || (draft.description || '') !== (original.description || '')
}

const cronButtonProps = { severity: 'secondary', size: 'small' }
const cronPopoverProps = { appendTo: 'body', baseZIndex: 2000 }

const pruneEdits = () => {
  const streams = workflow.value?.streams || []
  const streamIds = new Set(streams.map((stream) => stream.id).filter(Boolean))
  if (Object.keys(streamEdits.value).length) {
    const next = {}
    Object.entries(streamEdits.value).forEach(([id, draft]) => {
      if (streamIds.has(Number(id))) {
        next[id] = draft
      }
    })
    streamEdits.value = next
  }

  if (Object.keys(conditionEdits.value).length) {
    const conditionIds = new Set()
    streams.forEach((stream) => {
      (stream.conditions || []).forEach((cond) => {
        if (cond?.id) conditionIds.add(cond.id)
      })
    })
    const next = {}
    Object.entries(conditionEdits.value).forEach(([id, draft]) => {
      if (conditionIds.has(Number(id))) {
        next[id] = draft
      }
    })
    conditionEdits.value = next
  }
}

const severities = [
  { name: 'INFO' },
  { name: 'LOW' },
  { name: 'MEDIUM' },
  { name: 'HIGH' },
  { name: 'CRITICAL' }
]

const toNumberArray = (arr) => (arr || []).map(v => Number(v)).filter(v => !Number.isNaN(v))

const fetchWorkflowDetails = async () => {
  try {
    const { data } = await api.getWorkflow(props.workflowId, { lang: activeLang.value })
    workflow.value = {
      ...data,
      streams: Array.isArray(data?.streams) ? data.streams : [],
      tag_names: Array.isArray(data?.tag_names) ? data.tag_names : []
    }
    breadcrumbStore.setTitle(data?.name)
    pruneEdits()
  } catch (e) {
    notify.error(t('siemWorkflowDetailsNotifications.workflowLoadFailed'), e?.response?.data?.message || e.message)
  }
}


const fetchTags = async () => {
  try {
    const { data } = await api.getTags()
    // API liefert jetzt { tags: [], total, page, limit } mit Pagination
    const tagList = Array.isArray(data) ? data : (data?.tags ?? [])
    allTags.value = tagList.map(t => ({ id: Number(t.id), name: t.name }))
  } catch (e) {
    notify.error(t('siemWorkflowDetailsNotifications.tagsLoadFailed'), e?.response?.data?.message || e.message)
  }
}

const fetchMitreMappings = async () => {
  if (!props.workflowId) return
  mitreLoading.value = true
  mitreError.value = ''
  try {
    const { data } = await api.getWorkflowMitreMappings(props.workflowId)
    mitreMappings.value = Array.isArray(data) ? data : []
  } catch (e) {
    mitreError.value = e?.response?.data?.message || e.message
  } finally {
    mitreLoading.value = false
  }
}

const fetchIsacaMappings = async () => {
  if (!props.workflowId) return
  isacaLoading.value = true
  isacaError.value = ''
  try {
    const { data } = await api.getWorkflowIsacaMappings(props.workflowId)
    isacaMappings.value = Array.isArray(data) ? data : []
  } catch (e) {
    isacaError.value = e?.response?.data?.message || e.message
  } finally {
    isacaLoading.value = false
  }
}

const refreshAll = async () => {
  await Promise.all([fetchWorkflowDetails(), fetchTags(), fetchMitreMappings(), fetchIsacaMappings()])
}

const refreshStreams = async () => {
  if (!props.workflowId) return
  streamsLoading.value = true
  streamsError.value = ''
  try {
    const { data } = await api.getWorkflowStreams(props.workflowId)
    if (workflow.value) {
      workflow.value.streams = data || []
      pruneEdits()
    }
  } catch (e) {
    streamsError.value = e?.response?.data?.message || e.message
  } finally {
    streamsLoading.value = false
  }
}

const loadMitreTactics = async (force = false) => {
  if (mitreTactics.value.length && !force) return
  try {
    const { data } = await api.getMitreTactics({ limit: 500 })
    mitreTactics.value = Array.isArray(data) ? data : []
  } catch (e) {
    notify.error(t('siemWorkflowDetailsNotifications.mitreLoadFailed'), e?.response?.data?.message || e.message)
  }
}

const loadMitreTechniques = async (tacticId) => {
  if (!tacticId) {
    mitreTechniques.value = []
    mitreForm.value.technique_id = null
    return
  }
  try {
    const { data } = await api.getMitreTechniques({ tactic_id: tacticId, limit: 500 })
    mitreTechniques.value = Array.isArray(data) ? data : []
    if (!mitreEditing.value && !mitreTechniques.value.find((t) => t.id === mitreForm.value.technique_id)) {
      mitreForm.value.technique_id = null
    }
  } catch (e) {
    notify.error(t('siemWorkflowDetailsNotifications.mitreTechniquesFailed'), e?.response?.data?.message || e.message)
  }
}

const loadIsacaUseCases = async (force = false) => {
  if (isacaUseCases.value.length && !force) return
  try {
    const { data } = await api.getIsacaUseCases({ limit: 500 })
    isacaUseCases.value = Array.isArray(data) ? data : []
  } catch (e) {
    notify.error(t('siemWorkflowDetailsNotifications.isacaLoadFailed'), e?.response?.data?.message || e.message)
  }
}

const openCoreEdit = async () => {
  if (!workflow.value) return
  workflowLang.value = activeLang.value
  editingWorkflowCore.value = {
    severity: workflow.value.severity || 'INFO',
    tags: toNumberArray(workflow.value.tags),
    featured: !!workflow.value.featured,
    visibility: workflow.value.visibility || 'community',
  }
  baseWorkflowSnapshot.value = { name: '', description: '' }
  resetTranslationDrafts()
  setTranslationDraft('en', englishFallbackFromWorkflow(workflow.value), {
    setOriginal: true,
    onlyIfEmpty: true
  })
  showCoreDialog.value = true
  await loadWorkflowTranslations()
}

const loadWorkflowTranslations = async () => {
  if (!props.workflowId) return
  try {
    const [translationsRes, englishRes] = await Promise.all([
      api.getWorkflowTranslations(props.workflowId),
      api.getWorkflow(props.workflowId, { lang: 'en' })
    ])
    const translations = Array.isArray(translationsRes.data) ? translationsRes.data : []
    translations.forEach((item) => {
      setTranslationDraft(item.language_code, item, { setOriginal: true })
    })
    if (englishRes?.data) {
      baseWorkflowSnapshot.value = {
        name: englishRes.data.name || '',
        description: englishRes.data.description || ''
      }
      const hasEnglishOriginal = Boolean(
        (workflowTranslationOriginals.value.en?.name || '').trim() ||
        (workflowTranslationOriginals.value.en?.description || '').trim()
      )
      setTranslationDraft('en', baseWorkflowSnapshot.value, {
        setOriginal: !hasEnglishOriginal,
        onlyIfEmpty: true
      })
    }
    if (workflow.value?.name || workflow.value?.description) {
      setTranslationDraft(activeLang.value, {
        name: workflow.value.name || '',
        description: workflow.value.description || ''
      }, { onlyIfEmpty: true })
    }
    if (!baseWorkflowSnapshot.value.name && translationDraft('en')?.name) {
      baseWorkflowSnapshot.value = { ...translationDraft('en') }
    }
  } catch (e) {
    notify.error(t('siemWorkflowDetailsNotifications.workflowLoadFailed'), e?.response?.data?.message || e.message)
  }
}

const saveCore = async () => {
  const currentDraft = translationDraft(workflowLang.value)
  const englishDraft = translationDraft('en')
  const baseName = (englishDraft.name || baseWorkflowSnapshot.value.name || currentDraft.name || '').trim()
  const baseDescription = englishDraft.description || baseWorkflowSnapshot.value.description || currentDraft.description || ''

  const payload = {
    name: baseName,
    severity: editingWorkflowCore.value.severity,
    tags: toNumberArray(editingWorkflowCore.value.tags),
    featured: !!editingWorkflowCore.value.featured,
    visibility: editingWorkflowCore.value.visibility || 'community',
    description: baseDescription,
    language_code: 'en'
  }

  try {
    await api.updateWorkflow(props.workflowId, payload)
    await saveTranslationUpdates(props.workflowId)
    notify.success(t('siemWorkflowDetailsNotifications.workflowSaved'), t('siemWorkflowDetailsNotifications.workflowUpdated'))
  } catch (e) {
    notify.error(t('siemWorkflowDetailsNotifications.saveFailed'), e?.response?.data?.message || e.message)
  }

  showCoreDialog.value = false
  await fetchWorkflowDetails()
}

const saveTranslationUpdates = async (workflowId) => {
  const updates = []
  Object.entries(workflowTranslations.value).forEach(([lang, draft]) => {
    if (!draft || !translationHasContent(draft)) return
    if (!translationChanged(lang, draft)) return
    updates.push(api.updateWorkflowTranslation(workflowId, lang, {
      name: draft.name,
      description: draft.description
    }))
  })

  if (updates.length) {
    await Promise.all(updates)
  }
}

const openStreamDialog = () => {
  showAddStreamModal.value = true
}

const loadAvailableStreams = async (query = {}) => {
  try {
    availableLoading.value = true
    availableError.value = ''
    const params = { page: query.page || 1, limit: query.limit || 25 }
    if (query.search) params.search = query.search
    if (query.sortField) {
      params.sort_field = query.sortField
      params.sort_order = query.sortOrder || 'asc'
    }
    if (streamCategoryFilter.value) params.category = streamCategoryFilter.value
    // Scope: 'managed' → type, 'community'/'company' → visibility
    if (streamScopeFilter.value === 'managed') params.type = 'managed'
    else if (streamScopeFilter.value) params.visibility = streamScopeFilter.value
    const { data } = await api.getStreams(params)
    availableStreams.value = Array.isArray(data) ? data : (data?.data || [])
    availableTotal.value = data?.total ?? availableStreams.value.length
  } catch (e) {
    availableError.value = e?.response?.data?.message || e.message
  } finally {
    availableLoading.value = false
  }
}

const loadStreamCategories = async () => {
  try {
    const { data } = await api.getStreamCategories()
    streamCategories.value = data?.categories || []
  } catch {
    streamCategories.value = []
  }
}

// Filter geändert → Tabelle auf Seite 1 zurücksetzen und neu laden
const onStreamFilterChange = () => {
  streamPickerRef.value?.reload()
}

const isStreamLinked = (streamId) =>
  Array.isArray(workflow.value?.streams) &&
  workflow.value.streams.some((s) => s.id === streamId)

const attachStream = async (stream) => {
  if (!stream?.id) return
  try {
    streamsLoading.value = true
    streamsError.value = ''
    // stream_id funktioniert für verwaltete UND Custom-Streams;
    // Custom-Streams haben keine guid (die stammt von Enginsight).
    await api.addStreamToWorkflow(props.workflowId, {
      stream_id: stream.id,
      condition: ''
    })
    notify.success(t('siemWorkflowDetailsNotifications.workflowSaved'), t('siemWorkflowDetailsNotifications.workflowUpdated'))
    await refreshStreams()
  } catch (e) {
    streamsError.value = e?.response?.data?.message || e.message
  } finally {
    streamsLoading.value = false
  }
}

const removeStream = async (streamId) => {
  try {
    await api.removeStreamFromWorkflow(props.workflowId, streamId)
    notify.warn(t('siemWorkflowDetailsNotifications.streamRemoved'), t('siemWorkflowDetailsNotifications.workflowUpdated'))
    await refreshStreams()
  } catch (e) {
    notify.error(t('siemWorkflowDetailsNotifications.streamRemoveFailed'), e?.response?.data?.message || e.message)
  }
}

const moveStream = async (index, delta) => {
  if (!workflow.value?.streams) return
  const arr = [...workflow.value.streams]
  const newIndex = index + delta
  if (newIndex < 0 || newIndex >= arr.length) return
  ;[arr[index], arr[newIndex]] = [arr[newIndex], arr[index]]
  workflow.value.streams = arr

  try {
    streamsLoading.value = true
    streamsError.value = ''
    await api.reorderWorkflowStreams(props.workflowId, {
      stream_ids: arr.map((s) => s.id)
    })
  } catch (e) {
    notify.error(t('siemWorkflowDetailsNotifications.reorderFailed'), e?.response?.data?.message || e.message)
  } finally {
    streamsLoading.value = false
  }

  await refreshStreams()
}

const confidenceOptions = computed(() => [
  { label: 'LOW', value: 'LOW' },
  { label: 'MEDIUM', value: 'MEDIUM' },
  { label: 'HIGH', value: 'HIGH' }
])

const mitreTacticOptions = computed(() =>
  (mitreTactics.value || []).map((t) => ({
    ...t,
    label: t.domain ? `${t.domain.toUpperCase()} ${t.mitre_id} ${t.name}` : `${t.mitre_id} ${t.name}`
  }))
)

const mitreTechniqueOptions = computed(() =>
  (mitreTechniques.value || []).map((t) => ({
    ...t,
    label: t.mitre_id ? `${t.mitre_id} ${t.name}` : t.name
  }))
)

const isacaUseCaseOptions = computed(() =>
  (isacaUseCases.value || []).map((u) => ({
    ...u,
    label: `${u.code} ${u.name}`
  }))
)

const resetMitreForm = () => {
  mitreForm.value = {
    tactic_id: null,
    technique_id: null,
    confidence: 'MEDIUM',
    rationale: ''
  }
  mitreOriginal.value = { tactic_id: null, technique_id: null }
}

const openMitreCreate = async () => {
  mitreEditing.value = false
  resetMitreForm()
  await loadMitreTactics()
  showMitreDialog.value = true
}

const openMitreEdit = async (mapping) => {
  mitreEditing.value = true
  mitreOriginal.value = {
    tactic_id: mapping.tactic?.id || null,
    technique_id: mapping.technique?.id || null
  }
  mitreForm.value = {
    tactic_id: mapping.tactic?.id || null,
    technique_id: mapping.technique?.id || null,
    confidence: mapping.confidence || 'MEDIUM',
    rationale: mapping.rationale || ''
  }
  await loadMitreTactics()
  if (mitreForm.value.tactic_id) {
    await loadMitreTechniques(mitreForm.value.tactic_id)
  }
  showMitreDialog.value = true
}

const saveMitreMapping = async () => {
  if (!mitreForm.value.tactic_id) {
    notify.warn(t('siemWorkflowDetailsNotifications.mitreValidation'), t('siemWorkflowDetailsNotifications.mitrePickTactic'))
    return
  }
  const payload = {
    tactic_id: mitreForm.value.tactic_id,
    technique_id: mitreForm.value.technique_id || null,
    confidence: mitreForm.value.confidence,
    rationale: mitreForm.value.rationale || null
  }
  try {
    if (mitreEditing.value) {
      await api.updateWorkflowMitreMapping(props.workflowId, {
        tactic_id: mitreOriginal.value.tactic_id,
        technique_id: mitreOriginal.value.technique_id || null,
        new_technique_id: mitreForm.value.technique_id || null,
        confidence: payload.confidence,
        rationale: payload.rationale
      })
      notify.success(t('siemWorkflowDetailsNotifications.workflowSaved'), t('siemWorkflowDetailsNotifications.mitreUpdated'))
    } else {
      await api.createWorkflowMitreMapping(props.workflowId, payload)
      notify.success(t('siemWorkflowDetailsNotifications.workflowSaved'), t('siemWorkflowDetailsNotifications.mitreCreated'))
    }
    showMitreDialog.value = false
    await fetchMitreMappings()
  } catch (e) {
    notify.error(t('siemWorkflowDetailsNotifications.saveFailed'), e?.response?.data?.message || e.message)
  }
}

const deleteMitreMapping = async (mapping) => {
  if (!mapping?.tactic?.id) return
  if (!await confirmDelete(t('siemWorkflowDetailsNotifications.mitreDeleteConfirm'))) return
  try {
    const params = { tactic_id: mapping.tactic.id }
    if (mapping.technique?.id) params.technique_id = mapping.technique.id
    await api.deleteWorkflowMitreMapping(props.workflowId, params)
    notify.warn(t('siemWorkflowDetailsNotifications.mitreDeleted'), t('siemWorkflowDetailsNotifications.workflowUpdated'))
    await fetchMitreMappings()
  } catch (e) {
    notify.error(t('siemWorkflowDetailsNotifications.deleteFailed'), e?.response?.data?.message || e.message)
  }
}

const resetIsacaForm = () => {
  isacaForm.value = {
    use_case_id: null,
    confidence: 'MEDIUM',
    rationale: ''
  }
}

const openIsacaCreate = async () => {
  isacaEditing.value = false
  resetIsacaForm()
  await loadIsacaUseCases()
  showIsacaDialog.value = true
}

const openIsacaEdit = async (mapping) => {
  isacaEditing.value = true
  isacaForm.value = {
    use_case_id: mapping.use_case?.id || null,
    confidence: mapping.confidence || 'MEDIUM',
    rationale: mapping.rationale || ''
  }
  await loadIsacaUseCases()
  showIsacaDialog.value = true
}

const saveIsacaMapping = async () => {
  if (!isacaForm.value.use_case_id) {
    notify.warn(t('siemWorkflowDetailsNotifications.isacaValidation'), t('siemWorkflowDetailsNotifications.isacaPickUseCase'))
    return
  }
  const payload = {
    use_case_id: isacaForm.value.use_case_id,
    confidence: isacaForm.value.confidence,
    rationale: isacaForm.value.rationale || null
  }
  try {
    if (isacaEditing.value) {
      await api.updateWorkflowIsacaMapping(props.workflowId, payload)
      notify.success(t('siemWorkflowDetailsNotifications.workflowSaved'), t('siemWorkflowDetailsNotifications.isacaUpdated'))
    } else {
      await api.createWorkflowIsacaMapping(props.workflowId, payload)
      notify.success(t('siemWorkflowDetailsNotifications.workflowSaved'), t('siemWorkflowDetailsNotifications.isacaCreated'))
    }
    showIsacaDialog.value = false
    await fetchIsacaMappings()
  } catch (e) {
    notify.error(t('siemWorkflowDetailsNotifications.saveFailed'), e?.response?.data?.message || e.message)
  }
}

const deleteIsacaMapping = async (mapping) => {
  if (!mapping?.use_case?.id) return
  if (!await confirmDelete(t('siemWorkflowDetailsNotifications.isacaDeleteConfirm'))) return
  try {
    await api.deleteWorkflowIsacaMapping(props.workflowId, { use_case_id: mapping.use_case.id })
    notify.warn(t('siemWorkflowDetailsNotifications.isacaDeleted'), t('siemWorkflowDetailsNotifications.workflowUpdated'))
    await fetchIsacaMappings()
  } catch (e) {
    notify.error(t('siemWorkflowDetailsNotifications.deleteFailed'), e?.response?.data?.message || e.message)
  }
}

const streamTitle = (stream) =>
  stream?.name || stream?.guid || t('siemWorkflowDetails.streamDefault')

const conditionParts = (stream) => {
  const condition = (stream?.condition || '').trim()
  if (!condition) {
    return { type: emptyValue, field: emptyValue, operator: emptyValue, value: emptyValue, raw: '' }
  }
  const match = condition.match(/^\s*([^\s]+)\s+(==|=|!=|<=|>=|<|>|contains|in)\s+(.+)\s*$/i)
  if (match) {
    return {
      type: 'Filter',
      field: match[1],
      operator: match[2],
      value: match[3],
      raw: condition
    }
  }
  return { type: 'Filter', field: emptyValue, operator: emptyValue, value: condition, raw: '' }
}

const normalizeOperatorValue = (operator) => {
  if (!operator) return ''
  const normalized = operator.toString().toLowerCase()
  if (['neq', 'ungleich', 'not equal', '!=', '!=='].includes(normalized)) return 'neq'
  if (['eq', 'gleich', 'equal', '=', '=='].includes(normalized)) return 'eq'
  return operator
}

const normalizeCondition = (cond) => {
  if (!cond) {
    return { id: null, type: 'Filter', field: '', operator: '', value: '', cron: '' }
  }
  cond.type = cond.type || 'Filter'
  cond.field = cond.field ?? ''
  cond.operator = normalizeOperatorValue(cond.operator)
  cond.value = cond.value ?? ''
  cond.cron = cond.cron ?? ''
  return cond
}

const conditionList = (stream) => {
  if (Array.isArray(stream?.conditions) && stream.conditions.length) {
    return stream.conditions.map(normalizeCondition)
  }
  const parsed = conditionParts(stream)
  if (!parsed.raw) return []
  return [
    normalizeCondition({
      id: null,
      type: parsed.type,
      field: parsed.field,
      operator: parsed.operator,
      value: parsed.value,
      cron: ''
    })
  ]
}

const condRowClass = (type) => ({
  'condition-row--short': type !== 'Filter',
  'condition-row--time': type === 'Zeitfenster'
})

const conditionTypeOptions = computed(() => [
  { label: t('siemWorkflowDetails.conditionFilter'), value: 'Filter' },
  { label: t('siemWorkflowDetails.conditionGroup'), value: 'Group' },
  { label: t('siemWorkflowDetails.conditionDisplayField'), value: 'Display Field' },
  { label: t('siemWorkflowDetails.conditionTimeWindow'), value: 'Zeitfenster' }
])

const conditionOperatorOptions = computed(() => [
  { label: t('siemWorkflowDetails.operatorEqual'), value: 'eq' },
  { label: t('siemWorkflowDetails.operatorNotEqual'), value: 'neq' }
])

const conditionBaseValue = (cond, field) => {
  if (field === 'operator') return normalizeOperatorValue(cond?.operator)
  if (field === 'type') return cond?.type || 'Filter'
  return cond?.[field] ?? ''
}

const conditionFieldValue = (cond, field) => {
  if (!cond?.id) return conditionBaseValue(cond, field)
  const draft = conditionEdits.value[cond.id]
  if (draft && Object.prototype.hasOwnProperty.call(draft, field)) {
    return draft[field]
  }
  return conditionBaseValue(cond, field)
}

const updateConditionField = (stream, cond, field, value) => {
  if (!cond?.id) return
  const nextValue = value ?? ''
  const baseValue = conditionBaseValue(cond, field)
  const current = { ...(conditionEdits.value[cond.id] || {}) }
  if (nextValue == baseValue) {
    delete current[field]
  } else {
    current[field] = nextValue
  }
  if (Object.keys(current).length) {
    conditionEdits.value = { ...conditionEdits.value, [cond.id]: current }
  } else if (conditionEdits.value[cond.id]) {
    const next = { ...conditionEdits.value }
    delete next[cond.id]
    conditionEdits.value = next
  }
}



const resetConditionForm = (type = 'Filter') => {
  conditionForm.value = {
    type,
    field: '',
    operator: 'eq',
    value: '',
    cron: ''
  }
}

const updateConditionType = (type) => {
  resetConditionForm(type)
}

const openConditionModal = (stream) => {
  conditionTarget.value = stream
  resetConditionForm('Filter')
  showConditionModal.value = true
}

const closeConditionModal = () => {
  showConditionModal.value = false
  conditionTarget.value = null
}

const saveCondition = async () => {
  if (!conditionTarget.value || !props.workflowId) return
  const stream = conditionTarget.value
  const payload = {
    type: conditionForm.value.type,
    field: conditionForm.value.field,
    operator: conditionForm.value.operator,
    value: conditionForm.value.value,
    cron: conditionForm.value.cron
  }

  try {
    streamsLoading.value = true
    streamsError.value = ''
    await api.addStreamCondition(props.workflowId, stream.id, payload)
    notify.success(t('siemWorkflowDetailsNotifications.workflowSaved'), t('siemWorkflowDetailsNotifications.conditionCreated'))
    closeConditionModal()
    await refreshStreams()
  } catch (e) {
    streamsError.value = e?.response?.data?.message || e.message
  } finally {
    streamsLoading.value = false
  }
}

const deleteCondition = async (stream, cond) => {
  if (!cond?.id) return
  try {
    await api.deleteStreamCondition(props.workflowId, stream.id, cond.id)
    notify.warn(t('siemWorkflowDetailsNotifications.conditionDeleted'), t('siemWorkflowDetailsNotifications.workflowUpdated'))
    await refreshStreams()
  } catch (e) {
    notify.error(t('siemWorkflowDetailsNotifications.deleteFailed'), e?.response?.data?.message || e.message)
  }
}



const triggerOperatorIs = 'ist'
const triggerOperatorIsNot = 'ist nicht'

const streamBaseTrigger = (stream) => {
  const params = stream?.parameters || {}
  const rawOperator = (
    stream?.trigger_operator ??
    params.count_operator ??
    params.operator ??
    stream?.count_operator ??
    triggerOperatorIs
  ).toString()
  const operatorToken = rawOperator.toLowerCase()
  const operator = operatorToken.includes('nicht') || operatorToken == '!=' || operatorToken == 'not'
    ? triggerOperatorIsNot
    : triggerOperatorIs
  const rawCount = stream?.trigger_count ?? params.count ?? params.threshold ?? stream?.count ?? stream?.threshold ?? 1
  const count = Number.isFinite(Number(rawCount)) ? Number(rawCount) : 1
  return { operator, count }
}

const streamBaseValue = (stream, field) => {
  if (field === 'trigger_operator') return streamBaseTrigger(stream).operator
  if (field === 'trigger_count') return streamBaseTrigger(stream).count
  if (field === 'next_window_value') return nextWindowConfig(stream)?.value ?? null
  if (field === 'next_window_unit') return nextWindowConfig(stream)?.unit ?? null
  return stream?.[field] ?? null
}

const streamFieldValue = (stream, field) => {
  const draft = streamEdits.value[stream.id]
  if (draft && Object.prototype.hasOwnProperty.call(draft, field)) {
    return draft[field]
  }
  return streamBaseValue(stream, field)
}

const updateStreamField = (stream, field, value) => {
  if (!stream?.id) return
  const nextValue = value ?? null
  const baseValue = streamBaseValue(stream, field)
  const current = { ...(streamEdits.value[stream.id] || {}) }
  if (nextValue == baseValue) {
    delete current[field]
  } else {
    current[field] = nextValue
  }
  if (Object.keys(current).length) {
    streamEdits.value = { ...streamEdits.value, [stream.id]: current }
  } else if (streamEdits.value[stream.id]) {
    const next = { ...streamEdits.value }
    delete next[stream.id]
    streamEdits.value = next
  }
}


const triggerConfig = (stream) => {
  const operatorValue = streamFieldValue(stream, 'trigger_operator') || triggerOperatorIs
  const count = streamFieldValue(stream, 'trigger_count') || 1
  const operatorLabel = operatorValue == triggerOperatorIsNot ? t('siemWorkflowDetails.triggerOperatorIsNot') : t('siemWorkflowDetails.triggerOperatorIs')
  return { operatorLabel, operatorValue, count }
}


const operatorOptions = computed(() => [
  { label: t('siemWorkflowDetails.triggerOperatorIs'), value: triggerOperatorIs },
  { label: t('siemWorkflowDetails.triggerOperatorIsNot'), value: triggerOperatorIsNot }
])

const updateTriggerOperator = (stream, operator) => {
  if (!stream) return
  updateStreamField(stream, 'trigger_operator', operator)
}


const updateTriggerCount = (stream, value) => {
  if (!stream) return
  const parsed = Number(value)
  const count = Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 1
  updateStreamField(stream, 'trigger_count', count)
}


const nextWindowUnits = computed(() => [
  { label: t('siemWorkflowDetails.minutes'), value: 'minutes' },
  { label: t('siemWorkflowDetails.hours'), value: 'hours' }
])

const hasNextStream = (index) =>
  Array.isArray(workflow.value?.streams) && index < workflow.value.streams.length - 1

const nextStreamLabel = (index) => {
  if (!hasNextStream(index)) return t('siemWorkflowDetails.nextStreamDefault')
  const nextStream = workflow.value.streams[index + 1]
  return nextStream?.name || t('siemWorkflowDetails.nextStreamDefault')
}

const nextWindowConfig = (stream) => {
  const params = stream?.parameters || {}
  const rawValue = stream?.next_window_value ?? params.next_window_value ?? params.next_window ?? null
  const rawUnit = stream?.next_window_unit ?? params.next_window_unit ?? null
  if (rawValue == null || rawUnit == null) return null
  const value = Number.isFinite(Number(rawValue)) && Number(rawValue) > 0 ? Number(rawValue) : 10
  const unit = rawUnit === 'hours' ? 'hours' : 'minutes'
  return { value, unit }
}

const hasNextWindow = (stream) => Boolean(nextWindowConfig(stream))

const nextWindowDraft = (stream) => {
  const base = nextWindowConfig(stream)
  if (!base) return null
  return {
    value: streamFieldValue(stream, 'next_window_value') ?? base.value,
    unit: streamFieldValue(stream, 'next_window_unit') ?? base.unit
  }
}

const nextWindowUnitLabel = (unit) =>
  unit === 'hours' ? t('siemWorkflowDetails.hours') : t('siemWorkflowDetails.minutes')

const updateNextWindowValue = (stream, value) => {
  if (!stream) return
  const parsed = Number(value)
  const nextValue = Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 1
  updateStreamField(stream, 'next_window_value', nextValue)
  if (!streamFieldValue(stream, 'next_window_unit')) {
    updateStreamField(stream, 'next_window_unit', 'minutes')
  }
}


const updateNextWindowUnit = (stream, value) => {
  if (!stream) return
  const unit = value === 'hours' ? 'hours' : 'minutes'
  updateStreamField(stream, 'next_window_unit', unit)
  if (!streamFieldValue(stream, 'next_window_value')) {
    updateStreamField(stream, 'next_window_value', 10)
  }
}

const saveStreamChanges = async () => {
  if (!props.workflowId) return
  const streams = workflow.value?.streams || []
  const conditionToStream = new Map()
  streams.forEach((stream) => {
    (stream.conditions || []).forEach((cond) => {
      if (cond?.id) conditionToStream.set(cond.id, stream.id)
    })
  })

  const requests = []
  Object.entries(streamEdits.value).forEach(([id, draft]) => {
    const streamId = Number(id)
    if (!Number.isFinite(streamId)) return
    const payload = {
      ...(draft.trigger_operator !== undefined ? { trigger_operator: draft.trigger_operator } : {}),
      ...(draft.trigger_count !== undefined ? { trigger_count: draft.trigger_count } : {}),
      ...(draft.next_window_value !== undefined ? { next_window_value: draft.next_window_value } : {}),
      ...(draft.next_window_unit !== undefined ? { next_window_unit: draft.next_window_unit } : {}),
    }
    if (Object.keys(payload).length) {
      requests.push(api.updateWorkflowStream(props.workflowId, streamId, payload))
    }
  })

  Object.entries(conditionEdits.value).forEach(([id, draft]) => {
    const conditionId = Number(id)
    if (!Number.isFinite(conditionId)) return
    const streamId = conditionToStream.get(conditionId)
    if (!streamId) return
    const payload = {
      ...(draft.type !== undefined ? { type: draft.type } : {}),
      ...(draft.field !== undefined ? { field: draft.field } : {}),
      ...(draft.operator !== undefined ? { operator: draft.operator } : {}),
      ...(draft.value !== undefined ? { value: draft.value } : {}),
      ...(draft.cron !== undefined ? { cron: draft.cron } : {}),
    }
    if (Object.keys(payload).length) {
      requests.push(api.updateStreamCondition(props.workflowId, streamId, conditionId, payload))
    }
  })

  if (!requests.length) return

  try {
    changesSaving.value = true
    await Promise.all(requests)
    notify.success(t('siemWorkflowDetailsNotifications.workflowSaved'), t('siemWorkflowDetailsNotifications.streamSaveSuccess'))
    streamEdits.value = {}
    conditionEdits.value = {}
    await refreshStreams()
  } catch (e) {
    notify.error(t('siemWorkflowDetailsNotifications.saveFailed'), e?.response?.data?.message || e.message)
  } finally {
    changesSaving.value = false
  }
}

const hasUnsavedChanges = computed(() => (
  Object.keys(conditionEdits.value).length > 0 || Object.keys(streamEdits.value).length > 0
))

watch(hasUnsavedChanges, (value) => {
  unsavedChanges.setDirty(unsavedKey, value)
}, { immediate: true })

onBeforeUnmount(() => {
  unsavedChanges.setDirty(unsavedKey, false)
})



watch(activeLang, async (lang) => {
  if (!showCoreDialog.value) {
    workflowLang.value = lang
  }
  await refreshAll()
})

watch(() => props.workflowId, async () => {
  conditionEdits.value = {}
  streamEdits.value = {}
  await refreshAll()
}, { immediate: true })

watch(() => mitreForm.value.tactic_id, (val) => {
  loadMitreTechniques(val)
})

watch(showAddStreamModal, (open) => {
  if (open) {
    availableStreams.value = []
    availableTotal.value = 0
    streamCategoryFilter.value = null
    streamScopeFilter.value = null
    loadAvailableStreams()
    if (!streamCategories.value.length) loadStreamCategories()
  }
})
</script>

<template>
  <layoutShell>
    <div class="layout-content-wrapper">
      <main class="content">
        <div v-if="workflow">
          <!-- Page header -->
          <div class="wf-page-header">
            <div class="wf-page-header-left">
              <button class="wf-back-btn" @click="router.back()">
                <i class="pi pi-arrow-left" />
              </button>
              <WorkflowDetailsHeader :workflow="workflow" :can-edit="canEdit" @edit="openCoreEdit" />
            </div>
          </div>

          <div class="grid mt-3">
            <div class="col-12 lg:col-6">
              <div class="wf-sections">
                <WorkflowDetailsDescription :description="workflow.description" />
                <WorkflowDetailsTags :tags="workflow.tag_names || []" />
                <WorkflowDetailsMitreSection
                  :mappings="mitreMappings"
                  :loading="mitreLoading"
                  :error="mitreError"
                  :can-edit="canEdit"
                  @add="openMitreCreate"
                  @edit="openMitreEdit"
                  @delete="deleteMitreMapping"
                />
                <WorkflowDetailsIsacaSection
                  :mappings="isacaMappings"
                  :loading="isacaLoading"
                  :error="isacaError"
                  :can-edit="canEdit"
                  @add="openIsacaCreate"
                  @edit="openIsacaEdit"
                  @delete="deleteIsacaMapping"
                />
              </div>
            </div>

            <aside class="col-12 lg:col-6">
              <WorkflowDetailsStreamsSection
                :streams="workflow.streams || []"
                :streams-loading="streamsLoading"
                :streams-error="streamsError"
                :can-edit="canEdit"
                :changes-saving="changesSaving"
                :has-unsaved-changes="hasUnsavedChanges"
                :stream-title="streamTitle"
                :condition-list="conditionList"
                :condition-field-value="conditionFieldValue"
                :condition-type-options="conditionTypeOptions"
                :condition-operator-options="conditionOperatorOptions"
                :cond-row-class="condRowClass"
                :update-condition-field="updateConditionField"
                :trigger-config="triggerConfig"
                :operator-options="operatorOptions"
                :update-trigger-operator="updateTriggerOperator"
                :update-trigger-count="updateTriggerCount"
                :has-next-stream="hasNextStream"
                :has-next-window="hasNextWindow"
                :next-stream-label="nextStreamLabel"
                :next-window-draft="nextWindowDraft"
                :next-window-unit-label="nextWindowUnitLabel"
                :next-window-units="nextWindowUnits"
                :update-next-window-value="updateNextWindowValue"
                :update-next-window-unit="updateNextWindowUnit"
                :open-stream-dialog="openStreamDialog"
                :save-stream-changes="saveStreamChanges"
                :open-condition-modal="openConditionModal"
                :move-stream="moveStream"
                :remove-stream="removeStream"
                :delete-condition="deleteCondition"
              />
            </aside>
          </div>
        </div>
      </main>
    </div>
    <div class="layout-footer">
      <LayoutFooter />
    </div>
    <AppDialog
      v-model="showCoreDialog"
      :title="t('siemWorkflowDetails.editWorkflow')"
      size="xl"
      :show-footer="true"
      :maximizable="true"
      @submit="saveCore"
      @cancel="showCoreDialog = false"
    >
      <div class="dialog-form">
        <div class="form-row-2">
          <div class="form-field required">
            <label>{{ t('siemWorkflows.formName') }}</label>
            <InputText v-model="translationDraft(workflowLang).name" class="w-full" />
          </div>
          <div class="form-field">
            <label>{{ t('siemWorkflowDetails.workflowLanguage') }}</label>
            <Select v-model="workflowLang" :options="languageOptions" optionLabel="label" optionValue="code" class="w-full" />
          </div>
        </div>
        <div class="form-row-3">
          <div class="form-field">
            <label>{{ t('siemWorkflows.formSeverity') }}</label>
            <Select v-model="editingWorkflowCore.severity" :options="severities" optionLabel="name" optionValue="name" class="w-full" />
          </div>
          <div class="form-field">
            <label>{{ t('siemWorkflows.visibility') }}</label>
            <Select
              v-model="editingWorkflowCore.visibility"
              :options="[
                { label: t('siemWorkflows.visCompany'),   value: 'company'   },
                { label: t('siemWorkflows.visCommunity'), value: 'community' },
                { label: t('siemWorkflows.visPublic'),    value: 'public'    },
              ]"
              optionLabel="label"
              optionValue="value"
              class="w-full"
            />
          </div>
          <div class="form-field">
            <label>{{ t('siemWorkflows.formTags') }}</label>
            <MultiSelect v-model="editingWorkflowCore.tags" :options="allTags" optionLabel="name" optionValue="id" class="w-full" :maxSelectedLabels="3" display="chip" filter />
          </div>
        </div>
        <div class="form-field">
          <label class="flex align-items-center gap-2 cursor-pointer" style="flex-direction:row">
            <Checkbox v-model="editingWorkflowCore.featured" :binary="true" />
            {{ t('siemWorkflows.formFeatured') }}
          </label>
        </div>
        <div class="form-field">
          <label>{{ t('siemWorkflowDetails.description') }}</label>
          <Editor v-model="translationDraft(workflowLang).description" editor-style="min-height: 220px;" />
        </div>
      </div>
    </AppDialog>

    <AppDialog
      v-model="showAddStreamModal"
      :title="t('siemWorkflowDetails.streamAddDialogTitle')"
      size="xl"
      :show-footer="false"
      flush
    >
      <div v-if="availableError" class="picker-error">
        <i class="pi pi-exclamation-triangle" /> {{ availableError }}
      </div>
      <AppTable
        ref="streamPickerRef"
        class="picker-table"
        table-key="workflow-stream-picker"
        :columns="streamPickerColumns"
        :data="availableStreams"
        :total="availableTotal"
        :loading="availableLoading"
        lazy
        autofocus-search
        :default-rows="25"
        :row-class="(row) => isStreamLinked(row.id) ? 'picker-row-linked' : null"
        :empty-text="t('siemWorkflowDetails.streamNone')"
        @query-change="loadAvailableStreams"
        @refresh="loadAvailableStreams"
      >
        <template #toolbar>
          <Select
            v-model="streamScopeFilter"
            :options="streamScopeOptions"
            option-label="label"
            option-value="value"
            class="picker-filter"
            @change="onStreamFilterChange"
          />
          <Select
            v-model="streamCategoryFilter"
            :options="streamCategoryOptions"
            option-label="label"
            option-value="value"
            filter
            class="picker-filter"
            @change="onStreamFilterChange"
          />
        </template>
        <template #cell-name="{ row }">
          <span class="stream-picker-name">{{ row.name || row.guid }}</span>
        </template>
        <template #cell-category="{ row }">
          {{ row.category || '—' }}
        </template>
        <template #cell-subcategory="{ row }">
          {{ row.subcategory || '—' }}
        </template>
        <template #cell-company_name="{ row }">
          <Tag
            v-if="row.is_managed"
            :value="t('siemWorkflowDetails.streamScopeManaged')"
            severity="secondary"
          />
          <Tag
            v-else-if="row.visibility === 'community'"
            :value="t('siemWorkflowDetails.streamScopeCommunity')"
            severity="info"
          />
          <Tag
            v-else
            :value="row.company_name || t('siemWorkflowDetails.streamScopeCompany')"
            severity="success"
          />
        </template>
        <template #cell-guid="{ row }">
          <span class="stream-picker-guid">{{ row.guid }}</span>
        </template>
        <template #cell-actions="{ row }">
          <div class="picker-action-cell">
            <Button
              :icon="isStreamLinked(row.id) ? 'pi pi-check' : 'pi pi-plus'"
              size="small"
              :label="isStreamLinked(row.id) ? t('siemWorkflowDetails.streamLinked') : t('common.add')"
              :severity="isStreamLinked(row.id) ? 'secondary' : 'primary'"
              :outlined="isStreamLinked(row.id)"
              :disabled="isStreamLinked(row.id)"
              @click="attachStream(row)"
            />
          </div>
        </template>
      </AppTable>
    </AppDialog>

    <AppDialog
      v-model="showConditionModal"
      :title="t('siemWorkflowDetails.conditionAddDialogTitle')"
      size="md"
      :show-footer="true"
      @submit="saveCondition"
      @cancel="closeConditionModal"
    >
      <div class="dialog-form">
        <div class="form-field">
          <label>{{ t('siemWorkflowDetails.conditionType') }}</label>
          <Select
            :options="conditionTypeOptions"
            optionLabel="label"
            optionValue="value"
            :model-value="conditionForm.type"
            @update:model-value="(value) => updateConditionType(value)"
            class="w-full"
          />
        </div>

        <template v-if="conditionForm.type === 'Filter'">
          <div class="form-row-3">
            <div class="form-field">
              <label>{{ t('siemWorkflowDetails.conditionField') }}</label>
              <InputText v-model="conditionForm.field" class="w-full" />
            </div>
            <div class="form-field">
              <label>{{ t('siemWorkflowDetails.conditionOperator') }}</label>
              <Select :options="conditionOperatorOptions" optionLabel="label" optionValue="value" v-model="conditionForm.operator" class="w-full" />
            </div>
            <div class="form-field">
              <label>{{ t('siemWorkflowDetails.conditionValue') }}</label>
              <InputText v-model="conditionForm.value" class="w-full" />
            </div>
          </div>
        </template>

        <div v-else-if="conditionForm.type === 'Group' || conditionForm.type === 'Display Field'" class="form-field">
          <label>{{ t('siemWorkflowDetails.conditionField') }}</label>
          <InputText v-model="conditionForm.field" class="w-full" />
        </div>

        <div v-else-if="conditionForm.type === 'Zeitfenster'" class="form-field">
          <label>{{ t('siemWorkflowDetails.conditionCron') }}</label>
          <CronPrime
            v-model="conditionForm.cron"
            format="crontab"
            :locale="activeLang"
            :button-props="cronButtonProps"
            :popover-props="cronPopoverProps"
          />
        </div>
      </div>
    </AppDialog>

    <AppDialog
      v-model="showMitreDialog"
      :title="mitreEditing ? t('siemWorkflowDetails.mitreDialogTitleEdit') : t('siemWorkflowDetails.mitreDialogTitleCreate')"
      size="lg"
      :show-footer="true"
      @submit="saveMitreMapping"
      @cancel="showMitreDialog = false"
    >
      <div class="dialog-form">
        <div class="form-row-2">
          <div class="form-field">
            <label>{{ t('siemWorkflowDetails.mitreTactic') }}</label>
            <Select v-model="mitreForm.tactic_id" :options="mitreTacticOptions" optionLabel="label" optionValue="id" filter class="w-full" :disabled="mitreEditing" />
          </div>
          <div class="form-field">
            <label>{{ t('siemWorkflowDetails.mitreTechnique') }}</label>
            <Select v-model="mitreForm.technique_id" :options="mitreTechniqueOptions" optionLabel="label" optionValue="id" filter class="w-full" :showClear="!mitreEditing" />
          </div>
        </div>
        <div class="form-row-2">
          <div class="form-field">
            <label>{{ t('siemWorkflowDetails.mitreConfidence') }}</label>
            <Select v-model="mitreForm.confidence" :options="confidenceOptions" optionLabel="label" optionValue="value" class="w-full" />
          </div>
          <div class="form-field">
            <label>{{ t('siemWorkflowDetails.mitreRationale') }}</label>
            <InputText v-model="mitreForm.rationale" class="w-full" />
          </div>
        </div>
      </div>
    </AppDialog>

    <AppDialog
      v-model="showIsacaDialog"
      :title="isacaEditing ? t('siemWorkflowDetails.isacaDialogTitleEdit') : t('siemWorkflowDetails.isacaDialogTitleCreate')"
      size="lg"
      :show-footer="true"
      @submit="saveIsacaMapping"
      @cancel="showIsacaDialog = false"
    >
      <div class="dialog-form">
        <div class="form-field">
          <label>{{ t('siemWorkflowDetails.isacaUseCase') }}</label>
          <Select v-model="isacaForm.use_case_id" :options="isacaUseCaseOptions" optionLabel="label" optionValue="id" filter class="w-full" :disabled="isacaEditing" />
        </div>
        <div class="form-row-2">
          <div class="form-field">
            <label>{{ t('siemWorkflowDetails.isacaConfidence') }}</label>
            <Select v-model="isacaForm.confidence" :options="confidenceOptions" optionLabel="label" optionValue="value" class="w-full" />
          </div>
          <div class="form-field">
            <label>{{ t('siemWorkflowDetails.isacaRationale') }}</label>
            <InputText v-model="isacaForm.rationale" class="w-full" />
          </div>
        </div>
      </div>
    </AppDialog>
  </layoutShell>
</template>

<style scoped>
/* ── Page header ─────────────────────────────────────────────────── */
.wf-page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--p-content-border-color, rgba(0,0,0,.08));
}

.wf-page-header-left {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.wf-back-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid var(--p-content-border-color, rgba(0,0,0,.1));
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  color: var(--p-text-color-secondary);
  flex-shrink: 0;
  margin-top: 4px;
  transition: background .12s, color .12s;
}

.wf-back-btn:hover {
  background: var(--p-surface-100, rgba(0,0,0,.04));
  color: var(--p-text-color);
}

.wf-sections {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* ── Form (used in dialogs) ──────────────────────────────────────── */
.dialog-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-row-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.form-row-3 {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
}

@media (max-width: 640px) {
  .form-row-2, .form-row-3 { grid-template-columns: 1fr; }
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-field.required label::after {
  content: ' *';
  color: var(--p-red-500, #ef4444);
}

.form-field > label {
  font-size: .78rem;
  font-weight: 600;
  color: var(--p-text-color-secondary);
}

/* ── Stream picker ───────────────────────────────────────────────── */
.picker-state {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px 0;
  font-size: .875rem;
}

.muted {
  color: var(--p-text-color-secondary);
  opacity: 0.8;
}

.error-text {
  color: var(--p-red-500, #ef4444);
}

/* ── Dark mode ───────────────────────────────────────────────────── */
:global(.my-app-dark) .wf-page-header {
  border-bottom-color: var(--p-surface-700, #334155);
}

:global(.my-app-dark) .wf-back-btn {
  border-color: var(--p-surface-700, #334155);
}

:global(.my-app-dark) .wf-back-btn:hover {
  background: var(--p-surface-800, #1e293b);
}

.rationale {
  color: var(--text-color-secondary);
  font-size: 0.95rem;
}
</style>

<!-- Global: der Stream-Picker-Dialog wird via Teleport gerendert,
     scoped Styles greifen dort nicht. -->
<style>
.picker-error {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  font-size: 0.85rem;
  color: var(--p-red-500, #ef4444);
  border-bottom: 1px solid var(--p-content-border-color);
}

/* Tabelle randlos in den Dialog einpassen */
.app-dialog .app-table.picker-table {
  border: 0;
  border-radius: 0;
}
.app-dialog .app-table.picker-table .app-table-bar {
  padding: 12px 14px;
}

.stream-picker-name {
  font-weight: 600;
}
.stream-picker-guid {
  font-family: var(--font-family-mono, ui-monospace, SFMono-Regular, Menlo, monospace);
  font-size: 0.78rem;
  color: var(--p-text-color-secondary);
}
.picker-action-cell {
  display: flex;
  justify-content: flex-end;
}
.picker-filter {
  min-width: 11rem;
}
.app-dialog .app-table .picker-filter .p-select-label {
  font-size: 0.875rem;
  padding-block: 0.35rem;
}

/* Bereits verknüpfte Streams dezent ausgrauen */
.app-dialog .app-table .picker-row-linked > td {
  opacity: 0.55;
}
.app-dialog .app-table .picker-row-linked .stream-picker-name {
  font-weight: 500;
}
</style>
